/**
 * S8a. Deploys timeline.
 *
 * Purpose, in one line: show what happened, in order, and make it obvious
 * which version is the one people are actually visiting right now.
 *
 * THE ORGANIZING OBJECT IS THE LIVE DEPLOY, not the newest one, and on Studio
 * Ledger those are two different rows. The newest deploy did not work, so the
 * deploy from three days ago is what the world is looking at. Every console
 * that pins "latest" instead of "live" tells a person their app is broken when
 * it is fine. So the plate at the top of this page is the live version, with
 * its own web address and its own Visit action, and the timeline underneath is
 * history, in order, including the failure.
 *
 * THE ROLLBACK MODE TRAP is the one state on this screen that exists to
 * prevent a specific novice disaster. After a rollback, a person's newest code
 * is not live and nothing on a normal console says so, so they push a fix,
 * watch it deploy, and cannot understand why the site does not change. The
 * flat strip at the top of this page says it in one sentence and offers the
 * way out. It is derived, not remembered: if the live deploy is not the newest
 * one and the newest one did not fail, the app is on an older version on
 * purpose, and that is true whether or not this tab watched it happen.
 *
 * SIMPLE AND ADVANCED. This screen carries the most technical depth in the
 * product and used to be the only one with no way to reach any of it: the
 * trigger, the build identifier and the build output size were on the record
 * and printed nowhere. They are appended now, at the foot of the page, in one
 * disclosure, so they are one click away in Simple and already open in
 * Advanced. The branch and the hash on each row are the deploy row's own
 * Advanced layer, and the plate's hash follows the same rule, because an
 * unlabelled identifier is exactly what Simple exists to keep off the screen.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=deploying      a deploy in flight, above the live plate
 *   ?state=rolled-back    the trap strip, with &to=<deploy id> to pick which
 *   ?state=first-deploy   the one deploy case, with its inline line
 *   ?state=loading        the skeletons, held open
 *
 * // reads through environmentOf(); do not flatten.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Table } from '../../components/primitives/Table';
import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { DeployList, DeployRow, triggerWord, whenLabel } from '../../components/patterns/DeployRow';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Skeleton } from '../../components/patterns/Skeleton';
import { StatusPill } from '../../components/patterns/StatusPill';

import { environmentOf, getProject, listDeploys } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { bytes, shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { useTicker } from '../../lib/hooks/use-ticker';
import { t } from '../../lib/i18n/t';
import { scriptFor } from '../../lib/mock/scripts';
import { stateOf } from '../../lib/mock/simulate';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Deploy, Project } from '../../lib/types';

import '../../styles/components/deploys.css';

/** History is paged at twenty, per spec S8a zone 4. */
const PAGE = 20;

/**
 * A deploy that is still running cannot come out of the fixture: its start
 * time has to be "a moment ago" every time the state is opened, and a fixture
 * that carried a fixed start would show a deploy that had been building since
 * Tuesday. So this one record is built from the timeline the engine is already
 * driving. It is the only synthesized object on the screen, and everything
 * else on the page still comes from the seed.
 */
function inFlightDeploy(project: Project, startedAt: number): Deploy {
  return {
    id: 'dep_in_flight',
    projectId: project.id,
    status: 'deploying',
    commitSha: '4e1f7a2',
    commitMessage: 'Fix the spacing on the small screen layout',
    author: 'sam',
    branch: project.repo?.branch ?? 'main',
    createdAt: new Date(startedAt).toISOString(),
    // A deploy still running is not something you can put back yet.
    rollbackable: false,
    finishedAt: null,
    durationMs: null,
    isCurrent: false,
    errorGroupId: null,
  };
}

/**
 * Exactly one row in a timeline may say Live, and it is the version being
 * served. Both of this screen's fakeable transitions (a rollback landing, a
 * deploy landing) move which one that is without the fixture changing
 * underneath, so the lamps are derived here rather than read. Two rows
 * claiming Live is the exact contradiction the trap strip exists to prevent,
 * and printing it twice on one screen would be worse than not having built it.
 */
function demoteLive(list: Deploy[]): Deploy[] {
  return list.map((one) =>
    one.status === 'live' ? { ...one, status: 'replaced' as const, isCurrent: false } : one,
  );
}

function deployHref(slug: string, deploy: Deploy): string {
  return deploy.status === 'didnt-work'
    ? `/p/${slug}/deploys/${deploy.id}/failure`
    : `/p/${slug}/deploys/${deploy.id}`;
}

/* -------------------------------------------------------------- the plate */

/**
 * The live plate. It is a panel rather than a row because it answers a
 * different question from the rows underneath it: not "what happened" but
 * "what is my app running", and that question deserves the web address next
 * to the answer.
 */
function LivePlate({
  deploy,
  webAddress,
  slug,
  ink,
}: {
  deploy: Deploy;
  webAddress: string;
  slug: string;
  /** False when the trap strip owns the screen's one ink block. */
  ink: boolean;
}) {
  const host = webAddress.replace(/^https?:\/\//, '');
  const [advanced] = useAdvanced();

  return (
    <section className="plate" aria-label={t('The version your app is running')}>
      <div className="plate__head">
        <StatusPill value={deploy.status === 'deploying' ? 'deploying' : 'live'} live />
        <a className="plate__address mono" href={webAddress} target="_blank" rel="noreferrer">
          {host}
        </a>
      </div>

      <Link className="plate__message" to={`/p/${slug}/deploys/${deploy.id}`}>
        {deploy.commitMessage}
      </Link>

      {/* The hash is an identifier and it goes where every other identifier in
          the product goes. Printed bare in Simple it was a mono string with no
          label and no use, which is the exact thing Simple exists to keep off
          the screen. */}
      <p className="plate__meta">
        <span>{deploy.author}</span>
        {advanced ? <span className="mono">{shortSha(deploy.commitSha)}</span> : null}
        <span>{whenLabel(deploy.createdAt)}</span>
      </p>

      <div className="plate__actions">
        <Button
          variant={ink ? 'solid' : 'outline'}
          onClick={() => window.open(webAddress, '_blank', 'noreferrer')}
        >
          {t('Visit your app')}
        </Button>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- the screen */

export interface DeploysScreenProps {
  /**
   * True when this screen is rendered underneath the deploy drawer. It gives
   * up its ink block and stops arming timelines, because the drawer on top of
   * it owns both the next action and the one thing allowed to move.
   */
  behind?: boolean;
}

export function DeploysScreen({ behind = false }: DeploysScreenProps) {
  const slug = useProjectSlug() ?? '';
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const state = params.get('state');

  const [shown, setShown] = useState(PAGE);

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id;

  const { data: deploys } = useAsync(
    () => (projectId ? listDeploys(projectId) : Promise.resolve(undefined)),
    [projectId],
  );
  const all = useMemo(() => deploys ?? [], [deploys]);

  /* ---- the deploy in flight, driven by the one clock ---- */

  const timelines = useDemoStore((s) => s.timelines);
  const startTimeline = useDemoStore((s) => s.startTimeline);
  const now = useTicker();

  const wantsDeploying = state === 'deploying' && !behind;
  const lineId = `deploy-${slug}`;
  const line = timelines.find((one) => one.id === lineId);

  // Armed once per visit, and it restarts rather than resumes: timelines are
  // persisted, so a second look at this state would otherwise open on a deploy
  // that had already finished before the page was even on screen.
  const armedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!wantsDeploying || !project || armedFor.current === lineId) return;
    armedFor.current = lineId;
    startTimeline({
      id: lineId,
      kind: 'redeploy',
      scriptId: 'redeploy',
      subjectId: project.id,
      startedAt: Date.now(),
      outcome: 'success',
    });
  }, [wantsDeploying, project, lineId, startTimeline]);

  const run = wantsDeploying && line ? stateOf(line, now) : null;
  const running = run !== null && (run.status === 'running' || run.status === 'queued');
  const landed = run !== null && run.status === 'succeeded';

  const inFlight =
    wantsDeploying && project ? inFlightDeploy(project, line?.startedAt ?? now) : null;

  const progress =
    run && running
      ? {
          stepLabel: t(scriptFor('redeploy').steps[run.stepIndex].label),
          fraction: run.elapsedMs / run.totalMs,
        }
      : undefined;

  /* ---- what is live, and whether we are on an older version on purpose ---- */

  const environment = project ? environmentOf(project) : null;

  const forcedRollbackTo = state === 'rolled-back' ? params.get('to') : null;
  const currentId = environment?.currentDeployId;

  const newest = all[0];
  const live = all.find((d) => d.id === currentId);
  const trapped = newest && live && newest.id !== live.id;

  const pinned: Deploy | null = useMemo(() => {
    if (forcedRollbackTo) {
      const match = all.find((d) => d.id === forcedRollbackTo);
      if (match) return match;
    }
    return live ?? newest ?? null;
  }, [forcedRollbackTo, live, newest, all]);

  /* ---- the history list below the fold ---- */

  const history = useMemo(() => {
    const withoutPinned = pinned ? all.filter((d) => d.id !== pinned.id) : all;
    return withoutPinned;
  }, [all, pinned]);

  const rows = useMemo(() => {
    if (inFlight && running) return [inFlight, ...history].slice(0, shown);
    if (inFlight && landed) {
      return [{ ...inFlight, status: 'live' as const, isCurrent: true }, ...demoteLive(history)].slice(
        0,
        shown,
      );
    }
    return history.slice(0, shown);
  }, [inFlight, running, landed, history, shown]);

  const more = history.length - shown;

  useEffect(() => {
    if (project) {
      captureEvent('deploys_screen_viewed', {
        project_slug: project.slug,
        deploy_count: all.length,
        is_trapped: trapped,
      });
    }
  }, [project, all.length, trapped]);

  if ((isProjectLoading && !project) || state === 'loading') {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Deploys')}</h1>
        </header>
        <Skeleton shape="panel" count={1} label={t('Loading the version your app is running')} />
        <Skeleton shape="row" count={6} label={t('Loading your deploys')} />
      </div>
    );
  }

  // The shell renders for any slug, so a wrong one has to be answered rather
  // than left as a page that never finishes loading.
  if (!project || !environment) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Deploys')}</h1>
        </header>
        <EmptyState
          mark="box"
          sentence={t('We cannot find that project.')}
          action={
            <Button variant="outline" onClick={() => navigate('/projects')}>
              {t('Projects')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Deploys')}</h1>
      </header>

      {/* Flat, uncolored, and it stays until the newest version is back. A
          warning color here would say something is broken, and nothing is:
          the app is doing exactly what it was told to do. */}
      {trapped && live ? (
        <section className="trap" aria-label={t('This app is running an older version')}>
          <p className="trap__text">
            {t('Your app is running an older version on purpose. Your newest code is not live.')}
          </p>
          {/* The way out opens the newest deploy, where the confirm lives. The
              confirm lives in exactly one place on purpose: a second rollback
              dialog on this page would be a second copy of the most dangerous
              sentences in the product to keep right. */}
          {newest ? (
            <Button
              variant={behind ? 'outline' : 'solid'}
              onClick={() => navigate(`/p/${slug}/deploys/${newest.id}`)}
            >
              {t('Put the newest version back')}
            </Button>
          ) : null}
        </section>
      ) : null}

      {deploys === undefined ? (
        <>
          <Skeleton shape="panel" count={1} label={t('Loading the version your app is running')} />
          <Skeleton shape="row" count={6} label={t('Loading your deploys')} />
        </>
      ) : (
        <>
          {pinned ? (
            <LivePlate
              deploy={pinned}
              webAddress={environment.webAddress}
              slug={slug}
              ink={!behind && !trapped}
            />
          ) : null}

          <section className="deploys__history" aria-label={t('Deploy history')}>
            {rows.length === 0 ? (
              <EmptyState
                mark="box"
                sentence={t('Nothing has deployed yet.')}
                context={t('Your first deploy starts on its own, the next time you push to main.')}
              />
            ) : (
              <DeployList label={t('Deploy history')}>
                {rows.map((deploy) => (
                  <DeployRow
                    key={deploy.id}
                    deploy={deploy}
                    to={deploy.id === 'dep_in_flight' ? undefined : deployHref(slug, deploy)}
                    onRail
                    progress={deploy.id === 'dep_in_flight' ? progress : undefined}
                  />
                ))}
              </DeployList>
            )}

            {history.length === 1 ? (
              <p className="deploys__first">{t('This is your first deploy.')}</p>
            ) : null}

            {more > 0 ? (
              <div className="deploys__more">
                <Button variant="outline" onClick={() => setShown((count) => count + PAGE)}>
                  {t('Load more')}
                </Button>
              </div>
            ) : null}
          </section>

          {rows.length > 0 ? (
            <div className="deploys__advanced">
              <AdvancedDisclosure label={t('Show the build behind each deploy')}>
                <Table<Deploy>
                  caption={t('The build behind each deploy')}
                  rows={rows}
                  rowKey={(one) => one.id}
                  maxHeight="320px"
                  columns={[
                    {
                      key: 'sha',
                      header: t('Deploy'),
                      mono: true,
                      render: (one) => shortSha(one.commitSha),
                    },
                    {
                      key: 'trigger',
                      header: t('Started by'),
                      render: (one) => (one.trigger ? triggerWord(one.trigger) : ''),
                    },
                    {
                      key: 'build',
                      header: t('Build identifier'),
                      mono: true,
                      render: (one) => one.buildId ?? '',
                    },
                    {
                      key: 'output',
                      header: t('Build output'),
                      align: 'end',
                      mono: true,
                      render: (one) => (one.bundleBytes ? bytes(one.bundleBytes) : ''),
                    },
                  ]}
                />
              </AdvancedDisclosure>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function Deploys() {
  return <DeploysScreen />;
}
